import Pusher from 'pusher-js';
import {Config} from '../config';
import {Subject, Observable, BehaviorSubject} from 'rxjs';
import {Injectable} from '@angular/core';
import {HttpService} from './http.service';
import {HttpClient} from '@angular/common/http';
import {ChatResponseModel} from '../models/chat.model';
import {TokenStoreService} from './token-store.service';
import {ErrorHandlerService} from './error-handler.service';
import {NotificationService} from './notification.service';
import {UserModel} from "../models/user.model";
import {UserService} from "./user.service";

export type SubscriptionChangeEvent =
  {
    has_subscription: boolean;
    product_id: typeof Config.inAppProductsConfig.products[number]['id'] | null;
    expires: number | null;
    status: 'Active' | 'Cancelled' | 'Paused' | 'Ended' | null;
  } | {
  has_subscription: false;
  product_id?: undefined;
  expires?: undefined;
  status?: undefined;
};

@Injectable({
  providedIn: 'root'
})
export class PusherService extends HttpService {
  channel: any;
  notificationChannel: any;
  actionChannel: any;
  refreshChannel: any;
  private userId: number | undefined;

  private subject: Subject<any> = new Subject<any>();
  private refresh: Subject<any> = new Subject<any>();
  private notification: Subject<any> = new Subject<any>();

  private subscriptionChange$ = new BehaviorSubject<SubscriptionChangeEvent>({has_subscription: false});

  private pusherClient: any;

  constructor(
    http: HttpClient,
    private tokenService: TokenStoreService,
    private errorHandler: ErrorHandlerService,
    private notificationService: NotificationService,
    private userService: UserService
  ) {
    super(http);
    this.initializePusher();
  }


  /**
   * initialize pusher service
   * create instance of pusher that handles the functionality related to pusher
   */
  initializePusher() {
    this.tokenService.token.subscribe(token => {
      if (token && token.access_token) {
        this.pusherClient = new Pusher(Config.pusher.key, {
          cluster: Config.pusher.cluster,
          authEndpoint: this.buildUrl('broadcasting/auth'),
          forceTLS: true,
          authTransport: 'ajax',
          auth: {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${token.access_token}`,
              'X-App-Version': Config.appVersion
            },
            params: null
          }
        });
      }
    }, error => {
      this.errorHandler.handle(error);
    });

    this.userService.userData.subscribe((user) => {
      this.initSubscriptionChangeChannel(user);
    });
  }

  get subscriptionChange() {
    return this.subscriptionChange$.asObservable();
  }

  private initSubscriptionChangeChannel(user: UserModel | undefined) {
    // If the user id didn't change, we don't need to update anything.
    if (this.userId === user?.id) {
      return;
    }

    if (this.userId) {
      // Unsubscribe from old user channel
      this.pusherClient.unsubscribe(`private-user.${this.userId}`);
    }

    // Remember current user id
    this.userId = user?.id;

    if (this.userId) {
      // Subscribe to new user channel
      const channel = this.pusherClient.subscribe(`private-user.${this.userId}`);

      // Listen for subscription changes
      channel.bind(
        'subscription.change',
        (data: SubscriptionChangeEvent) => {
          this.subscriptionChange$.next(data);

          // Subscription changed, force a refresh of the user...
          this.userService.getUpdatedUser().subscribe();
        }
      );
    } else {
      this.subscriptionChange$.next({has_subscription: false});
    }
  }


  /**
   * will return subject as observable to subscribe to it
   * and in its subscription will receive new messages
   */
  getMessages(): Observable<any> {
    return this.subject.asObservable();
  }


  getRefresh(): Observable<any> {
    return this.refresh.asObservable();
  }

  getNotification(): Observable<any> {
    return this.notification.asObservable();
  }

  /**
   * Will establish connection for receiving messages
   * @param userId user id , it is being used here as channel name
   */
  initChat(chatId: string) {
    this.channel = this.pusherClient.subscribe('presence-chat.' + chatId);
    this.channel.bind('message.received', (data: ChatResponseModel) => {
      this.subject.next(data);
    }, error => {
      this.errorHandler.handle(error);
    });
  }

  /**
   * Unbind chat event
   * @param userId user id , it is being used here as channel name
   */
  unBindChatEvent(chatId: string) {
    this.channel.unbind('message.received');
    this.pusherClient.unsubscribe('presence-chat.' + chatId);
  }


  /**
   * Will establish connection for receiving messages
   * @param userId user id , it is being used here as channel name
   */
  refreshChat(userId: number) {
    this.refreshChannel = this.pusherClient.subscribe('private-chat.status.' + userId);
    this.refreshChannel.bind('unread.message', (data: ChatResponseModel) => {
      this.refresh.next(data);
    }, error => {
      this.errorHandler.handle(error);
    });
  }

  /**
   * Unbind refresh event
   * @param userId user id , it is being used here as channel name
   */
  unBindRefreshEvent(userId: number) {
    this.refreshChannel.unbind('unread.message');
    this.pusherClient.unsubscribe('private-chat.status.' + userId);
  }


  /**
   * Will establish connection for receiving notifications counter
   * @param userId user id , it is being used here as channel name
   */
  initNotificationCounter(userId: number) {
    if (userId) {
      this.notificationChannel = this.pusherClient.subscribe('private-user.notification.' + userId);
      this.notificationChannel.bind('notifications.count', data => {
        this.notification.next(data);
      }, error => {
        this.errorHandler.handle(error);
      });
    }
  }

  /**
   * Unbind notifications event
   * @param userId user id , it is being used here as channel name
   */
  unBindNotificationCounter(userId: number) {
    if (userId) {
      if (this.notificationChannel) {
        this.notificationChannel.unbind('notifications.count');
      }
      this.pusherClient.unsubscribe('private-user.notification.' + userId);
    }

  }

  setCounter() {
    this.notification.next({notifications_count: 0});
  }

  getCounterFromAPI() {
    this.notificationService.getNotificationCounter().subscribe((item: any) => {
      this.notification.next(item);
    });
  }


  initCounterMecha(userId: number) {
    this.getCounterFromAPI();
    this.initNotificationCounter(userId);
  }
}
